// Theme system type definitions

export type ThemeId = "paper" | "midnight" | "forest" | "sunset" | "ocean" | "pro" | "rose" | "lavender" | "mint" | "sand" | "playful" | "system";

export type ThemeCategory = "light" | "dark";

export type TextureType = "noise" | "dots" | "mesh" | "none";

export interface TextureConfig {
  type: TextureType;
  opacity: number;
}

export interface ThemeColors {
  // Backgrounds
  bg: string;
  surface: string;
  surface2: string;
  // Typography
  text: string;
  muted: string;
  textTertiary: string;
  textMuted: string;
  // Borders
  border: string;
  // Accent
  accent: string;
  accentText: string;
  // Brand colors
  forestGreen: string;
  forestGreenLight: string;
  forestGreenGlow: string;
  terracotta: string;
  terracottaGlow: string;
  // Danger
  danger: string;
  dangerBg: string;
  dangerText: string;
  // Status
  successBg: string;
  successText: string;
  warningBg: string;
  warningText: string;
  infoBg: string;
  infoText: string;
  // Overlay
  overlay: string;
  // Shadows
  shadow: string;
  shadowRing: string;
}

export interface ThemeConfig {
  id: Exclude<ThemeId, "system">;
  name: string;
  category: ThemeCategory;
  colors: ThemeColors;
  texture: TextureConfig;
  needsButtonRings: boolean;
  needsGlassEffect: boolean;
}

export interface ThemeContextValue {
  themeId: ThemeId;
  theme: ThemeConfig;
  isDark: boolean;
  needsButtonRings: boolean;
  needsGlassEffect: boolean;
  setTheme: (id: ThemeId) => void;
}

export const THEME_STORAGE_KEY = "cradleOS_theme";

// --- Theme configurations ---

// Paper - Current default, warm cream with forest green accent
const paperTheme: ThemeConfig = {
  id: "paper",
  name: "cradleOS Paper",
  category: "light",
  colors: {
    bg: "43 33% 97%",
    surface: "0 0% 100%",
    surface2: "40 25% 95%",
    text: "40 8% 10%",
    muted: "40 6% 36%",
    textTertiary: "40 6% 52%",
    textMuted: "40 4% 70%",
    border: "40 10% 88%",
    accent: "158 35% 27%",
    accentText: "0 0% 100%",
    forestGreen: "158 35% 27%",
    forestGreenLight: "158 35% 36%",
    forestGreenGlow: "158 35% 27% / 0.12",
    terracotta: "20 48% 56%",
    terracottaGlow: "20 48% 56% / 0.12",
    danger: "353 78% 54%",
    dangerBg: "355 85% 96%",
    dangerText: "352 65% 28%",
    successBg: "152 80% 95%",
    successText: "152 60% 28%",
    warningBg: "38 90% 94%",
    warningText: "30 70% 30%",
    infoBg: "210 80% 95%",
    infoText: "210 60% 30%",
    overlay: "0 0% 0%",
    shadow: "40 4% 10%",
    shadowRing: "40 4% 10%",
  },
  texture: { type: "none", opacity: 0 },
  needsButtonRings: false,
  needsGlassEffect: false,
};

// Midnight - Deep navy blues, bright blue accent
const midnightTheme: ThemeConfig = {
  id: "midnight",
  name: "Midnight",
  category: "dark",
  colors: {
    bg: "222 47% 8%",
    surface: "222 44% 16%",
    surface2: "222 40% 22%",
    text: "210 40% 96%",
    muted: "215 20% 58%",
    textTertiary: "215 15% 50%",
    textMuted: "215 10% 40%",
    border: "222 30% 26%",
    accent: "217 91% 60%",
    accentText: "0 0% 100%",
    forestGreen: "217 91% 60%",
    forestGreenLight: "217 91% 70%",
    forestGreenGlow: "217 91% 60% / 0.15",
    terracotta: "35 70% 60%",
    terracottaGlow: "35 70% 60% / 0.15",
    danger: "0 72% 55%",
    dangerBg: "0 50% 15%",
    dangerText: "0 80% 75%",
    successBg: "152 40% 14%",
    successText: "152 60% 70%",
    warningBg: "38 40% 14%",
    warningText: "38 70% 70%",
    infoBg: "210 40% 16%",
    infoText: "210 60% 72%",
    overlay: "0 0% 0%",
    shadow: "222 50% 3%",
    shadowRing: "217 30% 85%",
  },
  texture: { type: "dots", opacity: 0.15 },
  needsButtonRings: true,
  needsGlassEffect: false,
};

// Forest - Dark theme keeping forest green identity
const forestTheme: ThemeConfig = {
  id: "forest",
  name: "Forest",
  category: "dark",
  colors: {
    bg: "160 25% 8%",
    surface: "158 22% 16%",
    surface2: "156 20% 22%",
    text: "150 30% 96%",
    muted: "155 15% 50%",
    textTertiary: "155 10% 45%",
    textMuted: "155 8% 35%",
    border: "158 20% 24%",
    accent: "158 50% 50%",
    accentText: "0 0% 100%",
    forestGreen: "158 50% 50%",
    forestGreenLight: "158 50% 60%",
    forestGreenGlow: "158 50% 50% / 0.15",
    terracotta: "25 55% 55%",
    terracottaGlow: "25 55% 55% / 0.15",
    danger: "0 68% 50%",
    dangerBg: "0 45% 14%",
    dangerText: "0 75% 70%",
    successBg: "152 35% 14%",
    successText: "152 55% 68%",
    warningBg: "38 35% 14%",
    warningText: "38 65% 68%",
    infoBg: "210 35% 16%",
    infoText: "210 55% 70%",
    overlay: "0 0% 0%",
    shadow: "160 30% 3%",
    shadowRing: "158 25% 80%",
  },
  texture: { type: "none", opacity: 0 },
  needsButtonRings: true,
  needsGlassEffect: false,
};

// Sunset - Warm terracotta/orange, higher saturation light theme
const sunsetTheme: ThemeConfig = {
  id: "sunset",
  name: "Sunset",
  category: "light",
  colors: {
    bg: "30 50% 97%",
    surface: "35 45% 99%",
    surface2: "28 40% 94%",
    text: "20 25% 12%",
    muted: "25 15% 40%",
    textTertiary: "25 12% 50%",
    textMuted: "25 8% 65%",
    border: "30 25% 85%",
    accent: "20 65% 48%",
    accentText: "0 0% 100%",
    forestGreen: "20 65% 48%",
    forestGreenLight: "20 65% 58%",
    forestGreenGlow: "20 65% 48% / 0.15",
    terracotta: "20 65% 48%",
    terracottaGlow: "20 65% 48% / 0.15",
    danger: "0 75% 50%",
    dangerBg: "0 80% 95%",
    dangerText: "0 70% 30%",
    successBg: "152 70% 94%",
    successText: "152 55% 28%",
    warningBg: "38 80% 93%",
    warningText: "30 65% 30%",
    infoBg: "210 70% 94%",
    infoText: "210 55% 30%",
    overlay: "0 0% 0%",
    shadow: "25 15% 15%",
    shadowRing: "25 15% 15%",
  },
  texture: { type: "none", opacity: 0 },
  needsButtonRings: true,
  needsGlassEffect: false,
};

// Ocean - Cool blues/teals light theme
const oceanTheme: ThemeConfig = {
  id: "ocean",
  name: "Ocean",
  category: "light",
  colors: {
    bg: "195 35% 97%",
    surface: "200 30% 99%",
    surface2: "192 28% 94%",
    text: "200 25% 12%",
    muted: "195 15% 40%",
    textTertiary: "195 12% 52%",
    textMuted: "195 8% 68%",
    border: "195 20% 85%",
    accent: "187 65% 38%",
    accentText: "0 0% 100%",
    forestGreen: "187 65% 38%",
    forestGreenLight: "187 65% 48%",
    forestGreenGlow: "187 65% 38% / 0.12",
    terracotta: "195 55% 50%",
    terracottaGlow: "195 55% 50% / 0.12",
    danger: "355 72% 52%",
    dangerBg: "355 85% 96%",
    dangerText: "355 65% 28%",
    successBg: "152 75% 94%",
    successText: "152 55% 28%",
    warningBg: "38 80% 93%",
    warningText: "30 60% 30%",
    infoBg: "210 75% 94%",
    infoText: "210 55% 30%",
    overlay: "0 0% 0%",
    shadow: "200 20% 12%",
    shadowRing: "200 20% 12%",
  },
  texture: { type: "dots", opacity: 0.25 },
  needsButtonRings: false,
  needsGlassEffect: false,
};

// Pro - Dark glass with backdrop-blur, mesh gradient
const proTheme: ThemeConfig = {
  id: "pro",
  name: "Pro",
  category: "dark",
  colors: {
    bg: "240 10% 6%",
    surface: "240 8% 14%",
    surface2: "240 7% 20%",
    text: "0 0% 98%",
    muted: "240 5% 50%",
    textTertiary: "240 4% 45%",
    textMuted: "240 3% 36%",
    border: "240 8% 22%",
    accent: "262 80% 60%",
    accentText: "0 0% 100%",
    forestGreen: "262 80% 60%",
    forestGreenLight: "262 80% 70%",
    forestGreenGlow: "262 80% 60% / 0.18",
    terracotta: "328 70% 60%",
    terracottaGlow: "328 70% 60% / 0.18",
    danger: "0 70% 55%",
    dangerBg: "0 50% 12%",
    dangerText: "0 80% 75%",
    successBg: "152 35% 13%",
    successText: "152 55% 68%",
    warningBg: "38 35% 13%",
    warningText: "38 65% 68%",
    infoBg: "210 35% 15%",
    infoText: "210 55% 70%",
    overlay: "0 0% 0%",
    shadow: "240 15% 2%",
    shadowRing: "262 20% 85%",
  },
  texture: { type: "mesh", opacity: 0.3 },
  needsButtonRings: true,
  needsGlassEffect: true,
};

// Rose - Soft blush pink with rose gold accent
const roseTheme: ThemeConfig = {
  id: "rose",
  name: "Rose",
  category: "light",
  colors: {
    bg: "350 40% 97%",
    surface: "350 35% 99%",
    surface2: "350 30% 94%",
    text: "350 20% 12%",
    muted: "350 12% 40%",
    textTertiary: "350 10% 52%",
    textMuted: "350 8% 68%",
    border: "350 20% 88%",
    accent: "350 55% 55%",
    accentText: "0 0% 100%",
    forestGreen: "350 55% 55%",
    forestGreenLight: "350 55% 65%",
    forestGreenGlow: "350 55% 55% / 0.12",
    terracotta: "15 50% 55%",
    terracottaGlow: "15 50% 55% / 0.12",
    danger: "0 75% 52%",
    dangerBg: "0 85% 96%",
    dangerText: "0 65% 28%",
    successBg: "152 75% 94%",
    successText: "152 55% 28%",
    warningBg: "38 80% 93%",
    warningText: "30 60% 30%",
    infoBg: "210 75% 94%",
    infoText: "210 55% 30%",
    overlay: "0 0% 0%",
    shadow: "350 15% 12%",
    shadowRing: "350 15% 12%",
  },
  texture: { type: "none", opacity: 0 },
  needsButtonRings: false,
  needsGlassEffect: false,
};

// Lavender - Soft purple/lilac with violet accent
const lavenderTheme: ThemeConfig = {
  id: "lavender",
  name: "Lavender",
  category: "light",
  colors: {
    bg: "270 35% 97%",
    surface: "270 30% 99%",
    surface2: "270 25% 94%",
    text: "270 20% 12%",
    muted: "270 12% 40%",
    textTertiary: "270 10% 52%",
    textMuted: "270 8% 68%",
    border: "270 18% 88%",
    accent: "270 60% 55%",
    accentText: "0 0% 100%",
    forestGreen: "270 60% 55%",
    forestGreenLight: "270 60% 65%",
    forestGreenGlow: "270 60% 55% / 0.12",
    terracotta: "290 45% 55%",
    terracottaGlow: "290 45% 55% / 0.12",
    danger: "0 72% 52%",
    dangerBg: "0 85% 96%",
    dangerText: "0 65% 28%",
    successBg: "152 75% 94%",
    successText: "152 55% 28%",
    warningBg: "38 80% 93%",
    warningText: "30 60% 30%",
    infoBg: "210 75% 94%",
    infoText: "210 55% 30%",
    overlay: "0 0% 0%",
    shadow: "270 15% 12%",
    shadowRing: "270 15% 12%",
  },
  texture: { type: "noise", opacity: 0.3 },
  needsButtonRings: false,
  needsGlassEffect: false,
};

// Mint - Fresh mint green with teal accent
const mintTheme: ThemeConfig = {
  id: "mint",
  name: "Mint",
  category: "light",
  colors: {
    bg: "160 35% 97%",
    surface: "160 30% 99%",
    surface2: "160 25% 94%",
    text: "160 20% 12%",
    muted: "160 12% 40%",
    textTertiary: "160 10% 52%",
    textMuted: "160 8% 68%",
    border: "160 18% 88%",
    accent: "170 55% 40%",
    accentText: "0 0% 100%",
    forestGreen: "170 55% 40%",
    forestGreenLight: "170 55% 50%",
    forestGreenGlow: "170 55% 40% / 0.12",
    terracotta: "180 45% 45%",
    terracottaGlow: "180 45% 45% / 0.12",
    danger: "0 72% 52%",
    dangerBg: "0 85% 96%",
    dangerText: "0 65% 28%",
    successBg: "152 75% 94%",
    successText: "152 55% 28%",
    warningBg: "38 80% 93%",
    warningText: "30 60% 30%",
    infoBg: "210 75% 94%",
    infoText: "210 55% 30%",
    overlay: "0 0% 0%",
    shadow: "160 15% 12%",
    shadowRing: "160 15% 12%",
  },
  texture: { type: "dots", opacity: 0.2 },
  needsButtonRings: false,
  needsGlassEffect: false,
};

// Sand - Warm beige/tan with amber accent
const sandTheme: ThemeConfig = {
  id: "sand",
  name: "Sand",
  category: "light",
  colors: {
    bg: "38 40% 96%",
    surface: "38 35% 99%",
    surface2: "38 30% 92%",
    text: "38 25% 12%",
    muted: "38 15% 40%",
    textTertiary: "38 12% 52%",
    textMuted: "38 8% 68%",
    border: "38 22% 85%",
    accent: "38 70% 50%",
    accentText: "0 0% 100%",
    forestGreen: "38 70% 50%",
    forestGreenLight: "38 70% 60%",
    forestGreenGlow: "38 70% 50% / 0.12",
    terracotta: "25 60% 50%",
    terracottaGlow: "25 60% 50% / 0.12",
    danger: "0 72% 52%",
    dangerBg: "0 85% 96%",
    dangerText: "0 65% 28%",
    successBg: "152 75% 94%",
    successText: "152 55% 28%",
    warningBg: "38 80% 93%",
    warningText: "30 60% 30%",
    infoBg: "210 75% 94%",
    infoText: "210 55% 30%",
    overlay: "0 0% 0%",
    shadow: "38 20% 12%",
    shadowRing: "38 20% 12%",
  },
  texture: { type: "none", opacity: 0 },
  needsButtonRings: false,
  needsGlassEffect: false,
};

// Playful - Pastel rainbow with pink-purple accent
const playfulTheme: ThemeConfig = {
  id: "playful",
  name: "Playful",
  category: "light",
  colors: {
    bg: "320 30% 98%",
    surface: "320 25% 100%",
    surface2: "320 20% 95%",
    text: "320 20% 12%",
    muted: "320 12% 42%",
    textTertiary: "320 10% 52%",
    textMuted: "320 8% 68%",
    border: "320 18% 88%",
    accent: "310 65% 55%",
    accentText: "0 0% 100%",
    forestGreen: "310 65% 55%",
    forestGreenLight: "310 65% 65%",
    forestGreenGlow: "310 65% 55% / 0.15",
    terracotta: "280 55% 60%",
    terracottaGlow: "280 55% 60% / 0.15",
    danger: "0 72% 52%",
    dangerBg: "0 85% 96%",
    dangerText: "0 65% 28%",
    successBg: "152 75% 94%",
    successText: "152 55% 28%",
    warningBg: "38 80% 93%",
    warningText: "30 60% 30%",
    infoBg: "210 75% 94%",
    infoText: "210 55% 30%",
    overlay: "0 0% 0%",
    shadow: "320 15% 12%",
    shadowRing: "320 15% 12%",
  },
  texture: { type: "dots", opacity: 0.25 },
  needsButtonRings: false,
  needsGlassEffect: false,
};

// All themes map (excluding "system" which is a meta-option)
export const themes: Record<Exclude<ThemeId, "system">, ThemeConfig> = {
  paper: paperTheme,
  midnight: midnightTheme,
  forest: forestTheme,
  sunset: sunsetTheme,
  ocean: oceanTheme,
  pro: proTheme,
  rose: roseTheme,
  lavender: lavenderTheme,
  mint: mintTheme,
  sand: sandTheme,
  playful: playfulTheme,
};

// Ordered list of themes for the UI
export const themeList: ThemeConfig[] = [
  paperTheme,
  midnightTheme,
  forestTheme,
  sunsetTheme,
  oceanTheme,
  proTheme,
  roseTheme,
  lavenderTheme,
  mintTheme,
  sandTheme,
  playfulTheme,
];

// Default theme when System preference is dark
export const systemDarkDefault = midnightTheme;

// Default theme when System preference is light
export const systemLightDefault = paperTheme;
